<template>
	<div class="space-y-6 sm:space-y-8">
		<!-- Welcome Section -->
		<div class="card">
			<div class="card-body">
				<div class="flex flex-col sm:flex-row items-center sm:items-start space-y-5 sm:space-y-0 sm:space-x-8">
					<div class="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gradient-to-br from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover flex items-center justify-center text-white text-3xl sm:text-4xl font-bold shadow-elegant-lg transform transition-transform duration-300 hover:scale-105 ring-4 ring-primary/10 dark:ring-primary-dark/20">
						{{ userInitials }}
					</div>
					<div class="text-center sm:text-left flex-1">
						<h1 class="text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text">
						Welcome back, {{ userFullName }}!
						</h1>
						<p class="text-sm sm:text-base text-secondary-text/70 dark:text-secondary-dark-text/70 mt-2">
						{{ currentUser?.email }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Stats Cards -->
		<div class="grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
			<DashboardStatsCard
				v-if="isAdmin"
				title="Total Employees"
				:value="totalEmployees"
				:loading="loading"
				:icon="Users"
				color="primary"
			/>
			<DashboardStatsCard
				title="Completed Forms"
				:value="completedForms.length"
				:loading="loading"
				:icon="FileText"
				color="success"
			/>
			<DashboardStatsCard
				title="Materials"
				:value="assignedMaterials.length"
				:loading="loading"
				:icon="File"
				color="warning"
			/>
			<DashboardStatsCard
				v-if="!isAdmin"
				title="Pending Forms"
				:value="pendingForms"
				:loading="loading"
				:icon="Clock"
				color="info"
			/>
		</div>

		<!-- Quick Actions -->
		<div class="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-3">
			<QuickActionButton
				title="View Materials"
				description="Access learning resources"
				:icon="File"
				color="info"
				@click="handleViewMaterials"
			/>
			<QuickActionButton
				v-if="isAdmin"
				title="Completed Forms"
				description="Review submitted forms"
				:icon="FileText"
				color="success"
				@click="handleViewCompletedForms"
			/>
			<QuickActionButton
				v-if="isAdmin"
				title="View Employees"
				description="Manage employee accounts"
				:icon="Users"
				color="primary"
				@click="handleViewEmployees"
			/>
		</div>

		<!-- Analytics Chart -->
		<div class="card">
			<div class="card-header">
				<h2 class="text-lg sm:text-xl font-bold text-primary-text dark:text-primary-dark-text">
				Monthly Analytics
				</h2>
			</div>
			<div class="card-body">
				<div v-if="chartLoading" class="flex justify-center py-10">
					<Loader2 class="h-8 w-8 animate-spin text-primary" />
				</div>
				<div v-else class="w-full" style="height: 300px;">
					<Bar :data="chartData" :options="chartOptions" />
				</div>
			</div>
		</div>

		<!-- Recent Activity -->
		<div class="card">
			<div class="card-header">
				<h2 class="text-lg sm:text-xl font-bold text-primary-text dark:text-primary-dark-text">
				Recent Activity
				</h2>
			</div>
			<div class="card-body">
				<ActivityTimeline :activities="recentActivities" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useAuthStore } from '@/stores/auth'
import { completedFormService, type CompletedForm } from '@/services/completedFormService'
import { materialService } from '@/services/material.service'
import DashboardStatsCard from '@/components/dashboard/DashboardStatsCard.vue'
import QuickActionButton from '@/components/dashboard/QuickActionButton.vue'
import ActivityTimeline from '@/components/dashboard/ActivityTimeline.vue'
import { Loader2, Users, FileText, File, Clock } from 'lucide-vue-next'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')
useTitle(`${appTitle.value} - Dashboard`)

const router = useRouter()
const authStore = useAuthStore()
const { currentUser } = useUser()

const userFullName = computed (() => {
	const firstname = currentUser.value?.first_name || ''
	const lastname = currentUser.value?.last_name || ''

	return firstname && lastname ? `${firstname} ${lastname}` : (currentUser.value?.email || 'User')
})

const isAdmin = computed(() => authStore.user?.role === 'admin')

const userInitials = computed(() => {
	const name = currentUser.value?.name || currentUser.value?.first_name || ''
	
	return name
		.split(' ')
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)
})

const materials = ref<any[]>([])
const completedForms = ref<CompletedForm[]>([])
const loading = ref(false)
const chartLoading = ref(false)
const totalEmployees = ref(0)

const assignedMaterials = computed(() => {
  if (isAdmin.value) return materials.value
  const uid = currentUser.value?.id
  const pid = currentUser.value?.position_id
  if (!uid) return []
  return materials.value.filter((m: any) => {
    const posMatch = m.position_viewers?.some((p: any) => p.id === pid)
    const userMatch = m.user_viewers?.some((u: any) => u.id === uid)
    const noViewers = (!m.position_viewers || m.position_viewers.length === 0) &&
                      (!m.user_viewers || m.user_viewers.length === 0)
    return posMatch || userMatch || noViewers
  })
})

const pendingForms = computed(() => {
  if (isAdmin.value) return 0
  const uid = currentUser.value?.id
  if (!uid) return 0
  const completedIds = new Set(
    completedForms.value
      .filter(f => Number(f.user_id) === uid && f.fk_material_id)
      .map(f => Number(f.fk_material_id))
  )
  return assignedMaterials.value.filter(
    (m: any) => m.type === 'Online form' && !completedIds.has(m.id)
  ).length
})

const chartData = computed(() => {
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
	const now = new Date()
	const last6 = Array.from({ length: 6 }, (_, i) => {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
		return { label: months[d.getMonth()], year: d.getFullYear(), month: d.getMonth() }
	}).reverse()

	const formsByMonth = last6.map(m => {
		return completedForms.value.filter(f => {
			const d = toDate(f.date_sent || f.created_at)
			return d.getMonth() === m.month && d.getFullYear() === m.year
		}).length
	})

	const materialsByMonth = last6.map(m => {
		return assignedMaterials.value.filter((mat: any) => {
			const d = new Date(mat.created_at)
			return d.getMonth() === m.month && d.getFullYear() === m.year
		}).length
	})

	return {
		labels: last6.map(m => m.label),
		datasets: [
			{
				label: 'Completed Forms',
				backgroundColor: '#10b981',
				data: formsByMonth,
			},
			{
				label: 'Materials',
				backgroundColor: '#f59e0b',
				data: materialsByMonth,
			},
		],
	}
})

const chartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			position: 'bottom' as const,
		},
	},
	scales: {
		y: {
			beginAtZero: true,
			ticks: {
				stepSize: 1,
			},
		},
	},
}

const recentActivities = computed(() => {
	const activities: Array<{
		type: 'material' | 'form' | 'profile'
		description: string
		details?: string
		time: string
		datetime: string
	}> = []

	completedForms.value.slice(0, 5).forEach(form => {
		const date = toDate(form.date_sent || form.created_at)
		const employeeName = form.form_from || (form.user?.details?.first_name && form.user?.details?.last_name
			? `${form.user.details.first_name} ${form.user.details.last_name}`
			: form.user?.email || 'Unknown')
		activities.push({
			type: 'form',
			description: `Completed form: ${form.form_subject || form.material?.title || 'Form'}`,
			details: `Employee: ${employeeName}`,
			time: formatTimeAgo(date),
			datetime: form.date_sent || form.created_at || '',
		})
	})

	materials.value.slice(0, 5).forEach(mat => {
		const date = new Date(mat.created_at)
		activities.push({
			type: 'material',
			description: `${mat.type} added: ${mat.title}`,
			time: formatTimeAgo(date),
			datetime: mat.created_at || '',
		})
	})

	return activities.sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()).slice(0, 10)
})

const toDate = (val: string | undefined): Date => new Date(val ?? '')

const formatTimeAgo = (date: Date): string => {
	const now = new Date()
	const diff = now.getTime() - date.getTime()
	const days = Math.floor(diff / (1000 * 60 * 60 * 24))
	const hours = Math.floor(diff / (1000 * 60 * 60))
	const minutes = Math.floor(diff / (1000 * 60))

	if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`
	if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
	if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
	return 'Just now'
}

const handleViewMaterials = () => {
  	router.push(isAdmin.value ? '/admin/materials' : '/employee-materials')
}

const handleViewCompletedForms = () => {
  	router.push('/admin/completed-forms')
}

const handleViewEmployees = () => {
  	router.push('/employees')
}

onMounted(async () => {
	loading.value = true
	chartLoading.value = true
	try {
		const [mats, forms] = await Promise.all([
			materialService.fetchAll({ per_page: 1000 }).catch(() => [] as any),
			completedFormService.fetchAll().catch(() => []),
		])
		materials.value = Array.isArray(mats) ? mats : (Array.isArray((mats as any)?.data) ? (mats as any).data : [])
		completedForms.value = forms

		if (isAdmin.value) {
			try {
				const { data } = await import('@/services/api').then(m => m.default.get('/users?per_page=1'))
				totalEmployees.value = data?.meta?.total || data?.total || 0
			} catch {
				totalEmployees.value = 0
			}
		}
	} catch (error) {
		console.error('Failed to load dashboard data:', error)
	} finally {
		loading.value = false
		chartLoading.value = false
	}
})
</script>
